/**
 * EVERYTHING BEHIND SIGN-IN.
 *
 * The session is resolved once here and handed to every child route's context,
 * so each guard (`requirePerm`, `requireFeature`, `requireModule`) reads it
 * without a second request. The reader's layout is painted from the session
 * before the first frame.
 *
 * THE CIRCLE'S LOGO IS FETCHED HERE, NOT IN THE LAYOUT. `useQuery` is not on
 * the first-paint path (see `AppLayout` below — it costs about 4.3 kB gzipped
 * against the first-paint ceiling), so `beforeLoad` does the read with the query
 * client directly: the branch the reader is standing in
 * (`session.tenantId` / `session.storeId`), through the narrow `api/branch-logo.ts` read and
 * `branchLogoKeys`, which are imported from files that do not import
 * `hooks.ts`. The bytes become a `data:` URL through `logoSrc` — a PNG or
 * nothing — and ride the route context as `logoSrc`. A failed read, an empty
 * blob and a non-PNG all answer null and the circle keeps the initials; the
 * bar shows no error. The cache key is the one the branch dialogue's own read
 * shares, and the upload and delete hooks invalidate it — a logo save against
 * the signed-in branch then runs `router.invalidate()`, which lands back here
 * (the same pattern the header uses after a preference write).
 */
import { Outlet, createFileRoute, lazyRouteComponent, redirect } from "@tanstack/react-router";
import { Suspense } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { AppHeader } from "@/components/layout/app-header";
import { branchLogoKeys } from "@/features/settings/api/keys";
import { logoSrc } from "@/features/settings/api/logo-src";
import { readBranchLogo } from "@/features/settings/api/branch-logo";
import { useT } from "@/i18n";
import { sessionQuery, type Session } from "@/lib/auth/session";
import { useSearchHotkey, useSearchOpen } from "@/lib/search";
import { ViewProvider } from "@/lib/view-provider";

export const Route = createFileRoute("/_authenticated")({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.query(sessionQuery);
    if (!session) throw redirect({ to: "/login", search: { redirect: location.href } });
    return { session, logoSrc: await branchLogoSrc(context.queryClient, session) };
  },
  component: AppLayout,
});

/**
 * The signed-in branch's logo as a paintable URL, or null. The read is held a
 * minute so ordinary navigation does not refetch the file; an upload or
 * delete invalidates the key itself.
 */
async function branchLogoSrc(queryClient: QueryClient, session: Session): Promise<string | null> {
  if (!session.storeId) return null;
  try {
    const blob = await queryClient.query({
      queryKey: branchLogoKeys.logo(session.tenantId, session.storeId),
      queryFn: () => readBranchLogo(session.storeId),
      staleTime: 60_000,
    });
    return await logoSrc(blob);
  } catch {
    return null;
  }
}

/**
 * THE PALETTE IS FETCHED WHEN IT IS ASKED FOR, as the `?` sheet is
 * (`routes/__root.tsx`): the boolean is eager, the dialogue is not.
 */
const LazySearchPalette = lazyRouteComponent(
  () => import("@/components/layout/search-palette"),
  "SearchPalette",
);

function SearchPaletteHost({ session }: { session: Session }) {
  useSearchHotkey();
  const open = useSearchOpen();
  if (!open) return null;
  return (
    <Suspense>
      <LazySearchPalette session={session} />
    </Suspense>
  );
}

function AppLayout() {
  /**
   * THE ROUTE CONTEXT, AND NOT `useSession()`, WHICH COSTS 4.3 kB.
   *
   * The context is a snapshot `beforeLoad` resolved, so it does not change
   * again until the router re-runs. That was invisible while nothing on a
   * signed-in screen could change the session, and then the layout picker
   * arrived (21-09-2026): pressing *Windows grid view* wrote the store, wrote
   * the query cache, and repainted nothing, because this is what
   * `ViewProvider` reads.
   *
   * The obvious fix is `useSession()`. It was measured and put back:
   * `useQuery` is not otherwise on the first-paint path — `beforeLoad` uses
   * `queryClient.query`, which is the core and not the React binding — so
   * calling it HERE drags `useQuery`, `queryObserver`, `query` and
   * `infiniteQueryBehavior` into the eager chunk. 15 kB raw, 4.3 kB gzipped,
   * and it took the bundle over its ceiling.
   *
   * So the header calls `router.invalidate()` after it writes, `beforeLoad`
   * re-runs, and the cache it reads is the one that was just set. The cost of
   * that is re-running the loaders on a press that happens a few times a
   * week; the cost of the other is 4.3 kB on every first paint in the estate.
   */
  const { session, logoSrc } = Route.useRouteContext();
  const { t } = useT();
  return (
    <ViewProvider view={session.view}>
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-s2 focus:left-s2 focus:rounded-md focus:bg-surface focus:p-s2"
      >
        {t("app.skipToContent")}
      </a>
      <div className="flex h-dvh flex-col">
        <AppHeader session={session} logoSrc={logoSrc} />
        {/* A flex column, not a plain block: the flex-1 chain from here
            through each route's wrapper to PageShell's canvas is what makes
            the canvas the window's height rather than the content's — a
            content-height canvas clips an overlaying calendar at its own
            bottom edge (29-09-2026). */}
        <main id="main" className="flex min-h-0 flex-1 flex-col overflow-auto">
          <Outlet />
        </main>
      </div>
      <SearchPaletteHost session={session} />
    </ViewProvider>
  );
}
